// The content of this file is based on this article
// https://dev.to/paripsky/build-your-own-react-state-management-library-in-under-40-lines-of-code-with-typescript-support-hji
// The idea is to have a simple React state management library with TypeScript support.
// The main use (so far) of this store is to keep in the state, certain values that are shared between
// the components within the application. This will reduce a lot, sending extra parameters and functions
// between components.

import { useSyncExternalStore } from "react";
import {
  BabyBodyPositions,
  BabyHeadPositions,
  CephalicPositions,
} from "./Enums";

export type Listener = () => void;

function createStateManager<T>({ initialState }: { initialState: T }) {
  let subscribers: Listener[] = [];
  let state = initialState;

  const notifyStateChanged = () => {
    subscribers.forEach((fn) => fn());
  };

  return {
    subscribe(fn: Listener) {
      subscribers.push(fn);
      return () => {
        subscribers = subscribers.filter((listener) => listener !== fn);
      };
    },
    getSnapshot() {
      return state;
    },
    setState(newState: T) {
      state = newState;
      notifyStateChanged();
    },
  };
}

function createUseStateManager<T>(initialState: T) {
  const store = createStateManager({ initialState });
  return () =>
    [
      useSyncExternalStore(store.subscribe, store.getSnapshot),
      store.setState,
    ] as const;
}

// Reusable state managers

// Will keep the boolean value for resetting the calculator data when the avslutt
// button in the action bar is pressed. This store will serve as a bridge between the ActionBar and
// the Calculator components
export const resetCalculatorDataState = createUseStateManager<boolean>(false);

// Will keep the boolean value for resetting the visualization values when the avslutt
// button in the action bar is pressed. This store will serve as a bridge between the ActionBar and
// the Visualization components
export const resetVisualizationsDataState =
  createUseStateManager<boolean>(false);

// Will keep the value for the baby head position, that is used between the descend and the
// rotation visualization components.
export const babyHeadPositionState = createUseStateManager<string>(
  BabyHeadPositions.Centered
);

// Will keep the value for the baby body position, that is used between the descend and the
// rotation visualization components.
export const babyBodyPositionState = createUseStateManager<string>(
  BabyBodyPositions.Cephalic
);

// Will keep the value for the baby body rotation, that is used between the descend and the
// rotation visualization components.
export const babyBodyRotationState = createUseStateManager<string>(
  CephalicPositions.OcciputAnterior
);

// Will keep the value for the cervix opening, that is used between the descend and the
// rotation visualization components.
export const cervixOpeningState = createUseStateManager<string>("5");

// Will keep the value for the cervix descend, that is used in the descend visualization component.
export const babyDescendLevelState = createUseStateManager<number>(-5);

// Will keep the value for the refresh the labour data in the state. This will get trigger
// when the user add/delete observations from the data registered in the local storage
// and the changes will trigger changes in the visualizations and also in the progress player
// component
export const refreshLabourProgressDataFromStorageState =
  createUseStateManager<boolean>(false);

// Will keep the value for the progress player status (if active or not) and will be used in the
// application, to show up an overlay element in the DOM that will isolate the visualization components
// and the progress player, while the component is active. This will create an isolating view
// of those components, giving the focus of the animation and the progress player itself
export const progressPlayerModeActiveState =
  createUseStateManager<boolean>(false);
